import { forwardRef } from 'react';
import type { ElementType, HTMLAttributes } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Text.module.css';

export type TextSize =
  | '2xs'
  | 'xs'
  | 'sm'
  | 'base'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | 'hero';
export type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold';
export type TextColor =
  | 'text'
  | 'secondary'
  | 'muted'
  | 'onAccent'
  | 'accent'
  | 'warm'
  | 'positive'
  | 'critical';
export type TextFamily = 'body' | 'display' | 'figure';

const sizeClass: Record<TextSize, string | undefined> = {
  '2xs': styles.size2xs,
  xs: styles.sizeXs,
  sm: styles.sizeSm,
  base: styles.sizeBase,
  md: styles.sizeMd,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
  '2xl': styles.size2xl,
  '3xl': styles.size3xl,
  hero: styles.sizeHero,
};

const weightClass: Record<TextWeight, string | undefined> = {
  regular: styles.weightRegular,
  medium: styles.weightMedium,
  semibold: styles.weightSemibold,
  bold: styles.weightBold,
};

const colorClass: Record<TextColor, string | undefined> = {
  text: styles.colorText,
  secondary: styles.colorSecondary,
  muted: styles.colorMuted,
  onAccent: styles.colorOnAccent,
  accent: styles.colorAccent,
  warm: styles.colorWarm,
  positive: styles.colorPositive,
  critical: styles.colorCritical,
};

const familyClass: Record<TextFamily, string | undefined> = {
  body: styles.familyBody,
  display: styles.familyDisplay,
  figure: styles.familyFigure,
};

export interface TextProps extends HTMLAttributes<HTMLElement> {
  /** The tag to render. Defaults to `span`. */
  as?: ElementType;
  size?: TextSize;
  weight?: TextWeight;
  color?: TextColor;
  family?: TextFamily;
  /** Tabular figures, for numbers that should line up in a column. */
  tabular?: boolean;
}

export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  {
    as: Component = 'span',
    size = 'base',
    weight = 'regular',
    color = 'text',
    family = 'body',
    tabular = false,
    className,
    ...rest
  },
  ref,
) {
  return (
    <Component
      // `as` makes Component's element type a union, and a union of DOM
      // element ref targets doesn't collapse to something `ref` can satisfy
      // structurally. The prop itself stays strictly typed for consumers.
      ref={ref as never}
      className={cx(
        styles.text,
        sizeClass[size],
        weightClass[weight],
        colorClass[color],
        familyClass[family],
        tabular && 'tnum',
        className,
      )}
      {...rest}
    />
  );
});
